import {
  BootstrapSizes,
  Box,
  Button,
  ButtonColors,
  Grid,
  PageTitle,
  Row,
  Typography,
} from "@talentech/components";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  faviconsApi,
  useDeleteFaviconMutation,
  useGetFaviconsQuery,
} from "../../redux/reducer/favicons.api";
import { useDispatch } from "react-redux";
import ConfirmationModal from "@/components/ConfirmationModal/ConfirmationModal";

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

const faviconUploadUrl = `${process.env.API_URL}/favicon/`;

const TenantSettings: React.FC = () => {
  const { favicons } = useGetFaviconsQuery(
    {},
    {
      selectFromResult: ({ data, ...rest }) => {
        return {
          favicons: data?.files || [],
          ...rest,
        };
      },
    }
  );

  const [deleteFaviconModalOpen, setDeleteFaviconModalOpen] =
    useState<string>("");
  const [deleteFavicon] = useDeleteFaviconMutation();
  const onDeleteFavicon = () => {
    toast
      .promise(deleteFavicon(deleteFaviconModalOpen).unwrap(), {
        pending: "Deleting favicon...",
        success: "Favicon deleted successfully",
        error: "Failed to delete favicon",
      })
      .then(() => {
        setDeleteFaviconModalOpen("");
      });
  };

  const dispatch = useDispatch();

  return (
    <>
      <PageTitle title="Global settings" />
      <Grid container spacing={3} className="mt-4">
        <Row>
          <Grid  xs={12} md={6}>
            <Box paper rounded spacing={4}>
              <Typography variant="h2">Upload favicon</Typography>

              <Typography variant="p">
                Upload a favicon for your career site. The favicon is displayed
                in the browser tab.
              </Typography>

              <FilePond
                allowMultiple={false}
                acceptedFileTypes={[
                  "image/png",
                  "image/svg",
                  "image/x-icon",
                  "image/gif",
                  "image/vnd.microsoft.icon",
                ]}
                // maxFiles={3}
                server={{
                  process: {
                    url: faviconUploadUrl,
                    withCredentials: true,
                    method: "POST",
                  },
                }}
                name="file" /* sets the file input name, it's filepond by default */
                labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
                onprocessfiles={async () => {
                  toast.success("Favicon uploaded successfully");
                  dispatch(faviconsApi.util.invalidateTags(["Favicons"]));
                }}
              />
            </Box>
          </Grid>

          <Grid  xs={12} md={6}>
            <Box paper rounded spacing={4}>
              <Typography variant="h2">Favicons</Typography>
              <Typography variant="p">
                Here you can see all the favicons that have been uploaded.
              </Typography>

              <Grid container spacing={3}>
                <Row>
                  {favicons.map((favicon) => (
                    <Grid  xs={3} key={favicon.name}>
                      <Box className="position-relative">
                        <img
                          src={favicon.link}
                          alt={favicon.name}
                          className="img-fluid"
                        />
                        <Button
                          color={ButtonColors.DANGER}
                          onClick={() =>
                            setDeleteFaviconModalOpen(favicon.name)
                          }
                          icon={<i className="fa-sharp fa-light fa-trash"></i>}
                          outline
                          size={BootstrapSizes.SMALL}
                          className="position-absolute top-0 start-100 translate-middle"
                        />
                      </Box>
                    </Grid>
                  ))}
                </Row>
              </Grid>
            </Box>
          </Grid>
        </Row>
      </Grid>

      <ConfirmationModal
        show={!!deleteFaviconModalOpen}
        onClose={() => setDeleteFaviconModalOpen("")}
        onConfirm={onDeleteFavicon}
        title="Delete favicon"
        variant={ButtonColors.DANGER}
      >
        Are you sure you want to delete this favicon?
      </ConfirmationModal>
    </>
  );
};

export default TenantSettings;
